import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { mdApi } from '../../api/mdApi';
import './LunaTextPopup.css';

export default function LunaTextPopup({ patientId, patientName, activeConcern, concernId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Initialize with contextual first message or general welcome
  useEffect(() => {
    const welcomeText = activeConcern 
      ? `Hello! I'm Luna. I see you want to discuss ${patientName.split(' ')[0]}'s concern regarding "${activeConcern}". How can I assist you with their records or care plan today?`
      : `Hello! I'm Luna. I have ${patientName}'s clinical health profile loaded. How can I help you coordinate their care today?`;
      
    setMessages([{ role: 'assistant', content: welcomeText }]);
  }, [patientId, activeConcern]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || loading) return;

    const userText = inputText.trim();
    setInputText('');
    
    // Append user message immediately
    const updatedMessages = [...messages, { role: 'user', content: userText }];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      // Build API request payload via mdApi helper
      const data = await mdApi.chatWithLuna(patientId, {
        message: userText,
        active_concern: activeConcern,
        concern_id: concernId,
        history: messages.map(m => ({ role: m.role, content: m.content }))
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);

    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm sorry, I'm having trouble communicating with the server right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="luna-text-card">
      {/* Header */}
      <div className="luna-text-header">
        <div className="luna-avatar-info">
          <div className="luna-logo-dot" />
          <div>
            <h4>Luna Assistant</h4>
            <p>Patient Context: {patientName}</p>
          </div>
        </div>
        <button className="btn-close-chat" onClick={onClose} aria-label="Close Chat">
          <X size={16} />
        </button>
      </div>

      {/* Body / Scroll Area */}
      <div className="luna-text-body">
        {activeConcern && (
          <div className="concern-tag-banner">
            Active Symptom Focus: <strong>{activeConcern}</strong>
          </div>
        )}
        
        <div className="message-history-area">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message-row ${msg.role}`}>
              <div className="message-bubble">
                <ReactMarkdown allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'h1', 'h2', 'h3']}>
                  {msg.content}
                </ReactMarkdown>
              </div>
            </div>
          ))}
          {loading && (
            <div className="message-row assistant loading-bubble">
              <div className="message-bubble">
                <Loader className="spin-icon" size={16} />
                <span>Luna is thinking...</span>
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </div>

      {/* Footer / Send Message */}
      <form onSubmit={handleSendMessage} className="luna-text-footer">
        <input
          type="text"
          placeholder="Ask Luna about medications, vitals, scheduler..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={loading}
          aria-label="Message Input"
        />
        <button type="submit" disabled={!inputText.trim() || loading}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
